import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Archive, ArchiveRestore, CheckCircle2, Pencil, Plus } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CONTAINER_KIND_LABELS, CONTAINER_KIND_PLURALS, localDateString, stuckProjects } from '@cerebro-diem/core';
import type { ContainerKind, Note, ProjectStatus, Task } from '@cerebro-diem/core';
import { supabase } from '../../services/supabase';
import { errorMessage } from '../../services/errors';
import { createNote } from '../../services/notes';
import { LIST_LIMIT, queryKeys, reachedLimit } from '../../services/queries';
import {
  CONTAINER_COLUMNS,
  CONTAINER_PATHS,
  CONTAINER_TABLES,
  completeProject,
  renameContainer,
  setArchived,
  setProjectStatus,
  useContainers,
} from '../../services/containers';
import TaskItem from '../../components/TaskItem';
import { inputClass, outlineButton, primaryButton, tapLink } from '../../components/styles';
import { CONTAINER_ICONS, PROJECT_STATUSES, PROJECT_STATUS_LABELS } from './labels';
import LinkedFolder from './LinkedFolder';

interface ContainerDetailRow {
  id: string;
  name: string;
  description?: string | null;
  status?: ProjectStatus;
  archived_at: string | null;
}

/**
 * A note card's second line: the body's first line. Filing often writes the
 * body the same as the title, or starting with it, so a body that repeats the
 * title shows only what follows it, and nothing when that is all it says.
 * "Gate codes…" under the title "Gate code" is not a repeat: the word goes on.
 */
function notePreview(title: string, body: string | null): string | null {
  let text = (body ?? '').trim();
  const name = title.trim();
  if (name && text.startsWith(name) && !/^[\p{L}\p{N}]/u.test(text.slice(name.length))) {
    text = text.slice(name.length).replace(/^[\s:;,.\-–—]+/, '');
  }
  return text.split('\n')[0].trim() || null;
}

/** A Project, Area or Resource page; remounted per container so its forms start fresh. */
export default function ContainerDetailPage({ kind }: { kind: ContainerKind }) {
  const { id = '' } = useParams();
  return <ContainerDetail key={`${kind}:${id}`} kind={kind} id={id} />;
}

function ContainerDetail({ kind, id }: { kind: ContainerKind; id: string }) {
  const table = CONTAINER_TABLES[kind];
  const column = CONTAINER_COLUMNS[kind];
  const Icon = CONTAINER_ICONS[kind];
  const label = CONTAINER_KIND_LABELS[kind];
  const today = localDateString();
  const queryClient = useQueryClient();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState('');

  const containerQuery = useQuery({
    queryKey: queryKeys.container(table, id),
    queryFn: async () => {
      const { data, error } = await supabase.from(table).select('*').eq('id', id).maybeSingle();
      if (error) throw error;
      return (data ?? null) as ContainerDetailRow | null;
    },
  });
  const tasksQuery = useQuery({
    queryKey: queryKeys.containerTasks(id),
    enabled: kind !== 'resource',
    queryFn: async () => {
      const { data, error } = await supabase.from('tasks').select('*').eq(column, id).order('created_at').limit(LIST_LIMIT);
      if (error) throw error;
      return (data ?? []) as Task[];
    },
  });
  const notesQuery = useQuery({
    queryKey: queryKeys.containerNotes(id),
    queryFn: async () => {
      const { data, error } = await supabase.from('notes').select('*').eq(column, id).order('updated_at', { ascending: false }).limit(LIST_LIMIT);
      if (error) throw error;
      return (data ?? []) as Note[];
    },
  });
  const containersQuery = useContainers();

  const changed = () => Promise.all([queryKeys.containers, queryKeys.searches].map((queryKey) => queryClient.invalidateQueries({ queryKey })));
  const renameMutation = useMutation({
    mutationFn: () => renameContainer(kind, id, name),
    onSuccess: async () => {
      await changed();
      setRenaming(false);
    },
  });
  const archiveMutation = useMutation({ mutationFn: (archived: boolean) => setArchived(kind, id, archived), onSuccess: changed });
  const statusMutation = useMutation({ mutationFn: (status: ProjectStatus) => setProjectStatus(id, status), onSuccess: changed });
  const completeMutation = useMutation({ mutationFn: () => completeProject(id), onSuccess: changed });
  const busy = renameMutation.isPending || archiveMutation.isPending || statusMutation.isPending || completeMutation.isPending;
  const actionError = [renameMutation, archiveMutation, statusMutation, completeMutation].find((m) => m.isError)?.error;

  if (containerQuery.isError && containerQuery.data === undefined) {
    return <p className="text-sm text-error">Could not load this {label.toLowerCase()}: {errorMessage(containerQuery.error)}</p>;
  }
  if (containerQuery.isPending) return <p className="text-sm text-on-surface-variant">Loading…</p>;
  const container = containerQuery.data;
  if (!container) {
    return (
      <p className="text-on-surface-variant">
        This {label.toLowerCase()} was not found.{' '}
        <Link to={CONTAINER_PATHS[kind]} className="text-primary hover:underline">
          All {CONTAINER_KIND_PLURALS[kind].toLowerCase()}
        </Link>
      </p>
    );
  }

  const tasks = tasksQuery.data ?? [];
  const open = tasks.filter((t) => t.status !== 'done');
  const done = tasks.filter((t) => t.status === 'done');
  const notes = notesQuery.data ?? [];
  const isStuck =
    kind === 'project' &&
    tasksQuery.isSuccess &&
    stuckProjects([{ id, status: container.status ?? 'active', archived_at: container.archived_at }], tasks).has(id);
  const containers = containersQuery.data ?? [];

  const submitRename = (e: FormEvent) => {
    e.preventDefault();
    renameMutation.mutate();
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {containerQuery.isError && <p className="text-sm text-error">Could not refresh this {label.toLowerCase()}: {errorMessage(containerQuery.error)}</p>}
      {containersQuery.isError && <p className="text-sm text-error">Could not {containersQuery.data === undefined ? 'load' : 'refresh'} your projects and areas: {errorMessage(containersQuery.error)}</p>}
      <div className="space-y-2">
        <Link to={CONTAINER_PATHS[kind]} className={`${tapLink} text-sm text-primary hover:underline`}>
          ← {CONTAINER_KIND_PLURALS[kind]}
        </Link>
        {renaming ? (
          // On a phone the name takes the whole line and Save and Cancel go under it.
          <form onSubmit={submitRename} className="flex flex-wrap gap-2">
            <input
              className={`${inputClass} min-w-0 flex-1 basis-full sm:basis-0`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label={`${label} name`}
              required
            />
            <button type="submit" disabled={busy} className={primaryButton}>
              Save
            </button>
            <button type="button" onClick={() => setRenaming(false)} disabled={busy} className={outlineButton}>
              Cancel
            </button>
          </form>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <Icon className="w-8 h-8 text-primary" />
            <h1 className="min-w-0 text-2xl font-bold break-words">{container.name}</h1>
            <span className="text-sm text-on-surface-variant">{label}</span>
            {container.archived_at && <span className="text-sm text-tertiary font-medium">Archived</span>}
            <button
              type="button"
              onClick={() => {
                setName(container.name);
                setRenaming(true);
              }}
              aria-label={`Rename this ${label.toLowerCase()}`}
              className={outlineButton}
            >
              <Pencil className="w-4 h-4" />
            </button>
          </div>
        )}
        {container.description && <p className="text-on-surface-variant">{container.description}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {kind === 'project' && !container.archived_at && (
          <>
            <label className="text-sm text-on-surface-variant" htmlFor="project-status">
              Status
            </label>
            <select
              id="project-status"
              className={`${inputClass} w-auto`}
              value={container.status ?? 'active'}
              disabled={busy}
              onChange={(e) => {
                const next = PROJECT_STATUSES.find((s) => s === e.target.value);
                if (next) statusMutation.mutate(next);
              }}
            >
              {PROJECT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {PROJECT_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
            <button type="button" onClick={() => completeMutation.mutate()} disabled={busy} className={`${outlineButton} inline-flex items-center gap-1`}>
              <CheckCircle2 className="w-4 h-4" /> Complete &amp; archive
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => archiveMutation.mutate(!container.archived_at)}
          disabled={busy}
          className={`${outlineButton} inline-flex items-center gap-1`}
        >
          {container.archived_at ? (
            <>
              <ArchiveRestore className="w-4 h-4" /> Unarchive
            </>
          ) : (
            <>
              <Archive className="w-4 h-4" /> Archive
            </>
          )}
        </button>
      </div>
      {actionError !== undefined && <p className="text-sm text-error">Not saved: {errorMessage(actionError)}</p>}

      {isStuck && (
        <p className="flex items-center gap-2 text-sm text-error">
          <AlertTriangle className="w-4 h-4" /> No next action. Give this project one so it keeps moving.
        </p>
      )}

      {kind !== 'resource' && (
        <section className="bg-surface rounded-2xl p-4 sm:p-5 shadow-sm space-y-3">
          <h2 className="text-lg font-semibold">Tasks {tasksQuery.isSuccess && <span className="text-sm text-on-surface-variant">{open.length}</span>}</h2>
          {reachedLimit(tasksQuery.data) && (
            <p className="text-xs text-tertiary">Showing the first {LIST_LIMIT.toLocaleString()} tasks of this {label.toLowerCase()}.</p>
          )}
          {tasksQuery.isError && <p className="text-sm text-error">Could not {tasksQuery.data === undefined ? 'load' : 'refresh'} tasks: {errorMessage(tasksQuery.error)}</p>}
          {tasksQuery.isError && tasksQuery.data === undefined ? null : tasksQuery.isPending ? (
            <p className="text-sm text-on-surface-variant">Loading…</p>
          ) : open.length === 0 ? (
            <p className="text-sm text-on-surface-variant">No open tasks.</p>
          ) : (
            <ul className="space-y-2">
              {open.map((task) => (
                <li key={task.id}>
                  <TaskItem task={task} today={today} containers={containers} showContainer={false} />
                </li>
              ))}
            </ul>
          )}
          {done.length > 0 && (
            <details>
              <summary className="flex items-center min-h-11 pointer-fine:min-h-0 text-sm text-on-surface-variant cursor-pointer">Done ({done.length})</summary>
              <ul className="space-y-2 mt-2">
                {done.map((task) => (
                  <li key={task.id}>
                    <TaskItem task={task} today={today} containers={containers} showContainer={false} />
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>
      )}

      <section className="bg-surface rounded-2xl p-4 sm:p-5 shadow-sm space-y-3">
        <h2 className="text-lg font-semibold">Notes</h2>
        <AddNote kind={kind} containerId={id} />
        {reachedLimit(notesQuery.data) && (
          <p className="text-xs text-tertiary">Showing the first {LIST_LIMIT.toLocaleString()} notes of this {label.toLowerCase()}.</p>
        )}
        {notesQuery.isError ? (
          <p className="text-sm text-error">Could not load notes: {errorMessage(notesQuery.error)}</p>
        ) : notesQuery.isPending ? (
          <p className="text-sm text-on-surface-variant">Loading…</p>
        ) : notes.length === 0 ? (
          <p className="text-sm text-on-surface-variant">No notes.</p>
        ) : (
          <ul className="space-y-2">
            {notes.map((note) => {
              const preview = notePreview(note.title, note.body);
              return (
                <li key={note.id}>
                  <Link to={`/notes/${note.id}`} className="block rounded-xl p-3 bg-surface-variant/30 hover:bg-surface-variant/60">
                    <p className="font-medium break-words">{note.title}</p>
                    {preview && <p className="text-sm text-on-surface-variant truncate">{preview}</p>}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <LinkedFolder kind={kind} entityId={id} />
    </div>
  );
}

/**
 * "Add note": give it a title, and the new note, filed in this container,
 * opens for writing. A note needs a title, so a blank one writes nothing.
 */
function AddNote({ kind, containerId }: { kind: ContainerKind; containerId: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [validation, setValidation] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => createNote(kind, containerId, title),
    onSuccess: (noteId) => {
      navigate(`/notes/${noteId}`);
      // This container's notes, and the note counts on every container list.
      return Promise.all([queryKeys.notes, queryKeys.searches].map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
  });

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className={`${outlineButton} inline-flex items-center gap-1`}>
        <Plus className="w-4 h-4" aria-hidden="true" /> Add note
      </button>
    );
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setValidation('A note needs a title.');
      return;
    }
    setValidation(null);
    mutation.mutate();
  };

  return (
    <form onSubmit={submit} aria-label="Add a note" className="space-y-2">
      {/* On a phone the title takes the whole line and the buttons go under it. */}
      <div className="flex flex-wrap gap-2">
        <input
          className={`${inputClass} flex-1 min-w-0 basis-full sm:basis-0`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          aria-label="Note title"
          placeholder="Title"
          autoFocus
          required
        />
        <button type="submit" disabled={mutation.isPending} className={primaryButton}>
          {mutation.isPending ? 'Creating…' : 'Create and open'}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setTitle('');
            setValidation(null);
            mutation.reset();
          }}
          disabled={mutation.isPending}
          className={outlineButton}
        >
          Cancel
        </button>
      </div>
      <p className="text-xs text-on-surface-variant">The note opens next, for you to write the rest.</p>
      {validation && <p className="text-sm text-error">{validation}</p>}
      {mutation.isError && <p className="text-sm text-error">Not created: {errorMessage(mutation.error)}</p>}
    </form>
  );
}
